import { Fragment, JSX, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  PlannerConfigEntityWithOptionalFields,
  plannerWithDefaultValues,
} from '@/services/configurator/PlannerConfigTypes';
import { isObjectsEqual } from '@/utils/compare-json';
import { toast } from 'sonner';
import { PhraseForm } from './PhraseForm';
import { ConfigForm } from './ConfigForm';
import { Button } from '@/components/ui/button';
import { hasPermission } from '@/utils/validation';
import { SAVE_CHANGES_PERMISSIONS } from '@/config/feature-permissions';
import { useUser } from '@/components/providers/user-provider';
import { useEnv } from '@/components/providers/env-provider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ErrorStateIcon } from '../planner/ErrorStateIcon';
import {
  PlannerFormPhraseStringsSchema,
  PlannerEditFormSchema,
  PlannerAddFormSchema,
} from '@/types/PlannerFormSchema';
import { useTheme } from '@/components/providers/theme-provider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Mode } from '../planner/PlannerPageContainer';
import { isObjectEmpty } from '@/shared-utils/is-empty';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';

export type EditTabs = 'configuration' | 'translations';

export type PlannerSheetFormProps = {
  planner: PlannerConfigEntityWithOptionalFields;
  onSubmit: (data: PlannerConfigEntityWithOptionalFields) => void;
  mode: Mode;
  disabledPhraseFields: boolean;
  disabledFormFields: boolean;
  renderAdditionalHeaderContent?: () => JSX.Element;
  isSubmitting?: boolean;
};

// Used for both creating and editing an planner
export function EditForm({
  planner,
  onSubmit,
  mode,
  disabledPhraseFields,
  disabledFormFields,
  renderAdditionalHeaderContent,
  isSubmitting,
}: PlannerSheetFormProps) {
  const { role, editConfigEnabled } = useUser();
  const { env } = useEnv();
  const { theme } = useTheme();
  const [previewMode, setPreviewMode] = useState(false);
  const [tabView, setTabView] = useState<EditTabs>('configuration');
  const [hasCategoryCollision, setHasCategoryCollision] = useState(false);

  const plannerWithDefaults = useMemo(() => {
    if (mode === 'edit') {
      return plannerWithDefaultValues(planner);
    }
    return planner as PlannerConfigEntityWithOptionalFields;
  }, [planner.id, mode]);

  const form = useForm<PlannerConfigEntityWithOptionalFields>({
    mode: 'onChange',
    resolver: standardSchemaResolver(
      mode === 'edit' ? PlannerEditFormSchema : PlannerAddFormSchema,
    ),
    defaultValues: plannerWithDefaults,
    values: plannerWithDefaults,
  });

  const phraseForm = useForm<PlannerConfigEntityWithOptionalFields>({
    mode: 'onChange',
    resolver: standardSchemaResolver(PlannerFormPhraseStringsSchema),
    defaultValues: plannerWithDefaults,
    values: plannerWithDefaults,
  });

  let isButtonDisabled = false;
  if (tabView === 'configuration') {
    isButtonDisabled =
      disabledFormFields ||
      !hasPermission(SAVE_CHANGES_PERMISSIONS[env], role, editConfigEnabled) ||
      Object.keys(form.formState.errors).length >= 1;
  }
  if (tabView === 'translations') {
    isButtonDisabled =
      disabledFormFields ||
      !hasPermission(SAVE_CHANGES_PERMISSIONS[env], role, editConfigEnabled) ||
      isSubmitting ||
      Object.keys(phraseForm.formState.errors).length >= 1;
  }

  let buttonText = '';
  if (tabView === 'configuration') {
    buttonText = 'Next';
  }
  if (tabView === 'translations') {
    buttonText = 'Save Draft';
  }

  const handleSubmit = (
    plannerData: PlannerConfigEntityWithOptionalFields,
    phraseData: PlannerConfigEntityWithOptionalFields,
  ) => {
    const phraseStrings = phraseData.phraseStrings ?? {};
    const mergedData: PlannerConfigEntityWithOptionalFields = {
      ...plannerData,
      phraseStrings: { ...phraseStrings },
    };

    const isFormValuesSame = isObjectsEqual(
      { ...planner },
      {
        ...mergedData,
      },
    );
    if (isFormValuesSame) {
      toast.error('No changes have been made');
    } else {
      onSubmit(mergedData);
    }
  };

  const onButtonClicked = async () => {
    if (tabView === 'configuration') {
      await form.trigger().then((isFormValid) => {
        if (isFormValid) {
          setTabView('translations');
        }
      });
    }
    if (tabView === 'translations') {
      phraseForm.trigger().then((isPhraseFormValid) => {
        if (isPhraseFormValid) {
          handleSubmit(form.getValues(), phraseForm.getValues());
        }
      });
    }
  };
  const watchedShowInPages = form.watch('showInPages');
  const hasShowInPagesValues = (watchedShowInPages || [])?.length > 0;

  return (
    <Fragment>
      <div className='flex justify-between items-center flex-col md:flex-row'>
        <div className='flex flex-col lg:flex-row items-center justify-between pb-4 w-full'>
          {planner.identifier !== 'floor-calculator' && (
            <>
              <Tabs value={tabView} className='w-[400px] mt-4'>
                <TabsList
                  className={`grid w-full grid-cols-2 h-full ${hasShowInPagesValues ? 'grid-cols-2' : 'grid-cols-1'}`}
                >
                  <TabsTrigger
                    value='configuration'
                    onClick={() => setTabView('configuration')}
                  >
                    Configuration
                    {
                      <ErrorStateIcon
                        isValid={
                          form.formState.isValid && !hasCategoryCollision
                        }
                      />
                    }
                  </TabsTrigger>
                  {hasShowInPagesValues && (
                    <TabsTrigger
                      value='translations'
                      onClick={() => setTabView('translations')}
                      disabled={!form.formState.isValid}
                    >
                      Translations
                      <ErrorStateIcon isValid={phraseForm.formState.isValid} />
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            </>
          )}
        </div>
        <>
          {renderAdditionalHeaderContent && (
            <div className='flex flex-col lg:flex-row items-center justify-between pb-4'>
              {renderAdditionalHeaderContent()}
            </div>
          )}
        </>
      </div>
      <div className='overflow-y-scroll max-h-[75vh] pb-[100px]'>
        <div
          className={`${tabView === 'configuration' ? 'visible' : 'hidden'}`}
        >
          <ConfigForm
            form={form}
            initialState={plannerWithDefaults}
            mode={mode}
            disabledFormFields={disabledFormFields}
            onCollisionChange={setHasCategoryCollision}
          />
        </div>
        <div className={`${tabView === 'translations' ? 'visible' : 'hidden'}`}>
          <PhraseForm
            isFloorCalculator={planner.identifier === 'floor-calculator'}
            form={form}
            phraseForm={phraseForm}
            previewMode={previewMode}
            disabledFields={disabledPhraseFields}
            identifier={planner.identifier}
          />
        </div>
        <div
          className={`fixed bottom-0 left-0  p-4 w-full flex border-t border-gray-300 ${theme === 'dark' ? 'bg-background border-input' : 'bg-white'} flex ${tabView === 'translations' ? 'justify-between' : 'justify-end'} items-center`}
        >
          {tabView === 'translations' && (
            <div className='flex justify-between items-center'>
              <Switch
                id='preview-mode'
                onCheckedChange={(checked) => setPreviewMode(checked)}
                className='mr-2'
              />
              <Label htmlFor='preview-mode'>Toggle preview mode</Label>
            </div>
          )}
          <div className='flex justify-between items-center'>
            {!isObjectEmpty(form.formState.errors) && (
              <p className='mr-4'>
                Uh-oh! It looks like some fields need your attention. Please
                review and submit again.
              </p>
            )}
            {planner.identifier !== 'floor-calculator' && (
              <Button
                type='submit'
                disabled={isButtonDisabled}
                isLoading={isSubmitting}
                onClick={onButtonClicked}
              >
                {buttonText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
}
